/**
 * Settings → Integrations (admin only; App.js wraps it in <AdminRoute> and every /api/integrations call
 * requires the admin role on the server).
 *
 * One card per API connection (sales, or ad spend for Profitability): status, last / next sync, last error, auto-sync switch,
 * Test, Sync now, Edit, Remove, and the recent sync runs (with any unmatched marketplace codes).
 * Credentials are write-only: the server returns secrets masked, and a blank secret on edit keeps it.
 * Connectors that are not built yet are listed as "Coming soon".
 * Codes from API sales and returns that match no SKU are kept (not counted) and wait in the Code Mappings queue;
 * a banner here shows how many and links to it.
 * Sales connections show their cut-over date (from then on the API is the only source of that platform's
 * sales; Excel uploads for those dates are skipped) and offer a one-time backfill with preview and undo.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Clock, History, Link2, Pencil, PlayCircle, Plug, Plus, RefreshCw, Trash2, Undo2, Wifi } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCountry } from '../contexts/CountryContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { PLATFORMS } from '../config/platforms';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/integrations`;

const STATUS_TONE = {
  OK: 'bg-green-100 text-green-800',
  ERROR: 'bg-red-100 text-red-800',
  RUNNING: 'bg-blue-100 text-blue-800',
  NEW: 'bg-gray-100 text-gray-700',
};
const platformName = code => PLATFORMS[code]?.name || code;
const when = value => (value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const rupees = value => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
const RETURNS_API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/returns/v2`;
const ALIAS_API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/sku-aliases`;
const defaultLookback = kind => ({ ads: 30, returns: 45 }[kind] || 3);
const REPORT_NAMES = { FBA: 'FBA returns', MFN: 'Seller-fulfilled returns' };

const ConnectionDialog = ({ open, onOpenChange, connectors, editing, country, onSaved, items = [] }) => {
  const available = connectors.filter(c => c.available);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({ connector: editing.connector, name: editing.name, platform: editing.platform,
        credentials: {}, ...editing.settings, cutover_date: editing.cutover_date || '', enabled: editing.enabled });
    } else {
      const first = available[0];
      setForm({ connector: first?.key || '', name: '', platform: first?.platforms[0] || '', credentials: {},
        sync_interval_minutes: first?.kind === 'returns' ? 360 : 60, lookback_days: defaultLookback(first?.kind), enabled: false });
    }
  }, [open, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return null;
  const connector = connectors.find(c => c.key === form.connector);
  const set = patch => setForm(prev => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    const settings = { sync_interval_minutes: Number(form.sync_interval_minutes), lookback_days: Number(form.lookback_days) };
    if ((form.cutover_date || null) !== (editing?.cutover_date || null)) settings.cutover_date = form.cutover_date || null;
    try {
      if (editing) {
        await axios.put(`${API}/${editing.id}`, { name: form.name, settings, credentials: form.credentials });
      } else {
        await axios.post(API, { connector: form.connector, name: form.name, platform: form.platform, country,
          credentials: form.copy_from ? {} : form.credentials, settings, enabled: form.enabled,
          copy_credentials_from: form.copy_from || null });
      }
      toast.success(editing ? 'Connection updated' : 'Connection added');
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the connection'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="integration-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add a connection'}</DialogTitle>
          <DialogDescription>
            Keys are stored encrypted and are never shown again. {editing && 'Leave a secret blank to keep the saved one.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <div className="space-y-1">
              <Label>Data source</Label>
              <select className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm" value={form.connector}
                onChange={event => { const next = connectors.find(c => c.key === event.target.value); set({ connector: next.key, platform: next.platforms[0], credentials: {}, copy_from: '', lookback_days: defaultLookback(next.kind), sync_interval_minutes: next.kind === 'returns' ? 360 : 60 }); }}>
                {available.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
              {connector?.description && <p className="text-xs text-gray-500">{connector.description}</p>}
            </div>
          )}
          <div className="space-y-1">
            <Label>Connection name</Label>
            <Input value={form.name} onChange={event => set({ name: event.target.value })} placeholder="e.g. Amazon — Drogo account" />
          </div>
          {!editing && connector && connector.platforms.length > 1 && (
            <div className="space-y-1">
              <Label>{connector.kind === 'ads' ? 'These ads drive sales on' : 'Sales platform'}</Label>
              <select className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm" value={form.platform}
                onChange={event => set({ platform: event.target.value })}>
                {connector.platforms.map(code => <option key={code} value={code}>{platformName(code)}</option>)}
              </select>
            </div>
          )}
          {!editing && (() => {
            // Connections whose saved keys cover what this connector needs (e.g. Amazon sales -> Amazon returns).
            const needed = (connector?.fields || []).filter(f => f.required).map(f => f.key);
            const familyOf = key => connectors.find(c => c.key === key)?.credential_family;
            const donors = items.filter(i => i.connector_available && needed.length && connector?.credential_family &&
              familyOf(i.connector) === connector.credential_family &&
              needed.every(key => Object.prototype.hasOwnProperty.call(i.credentials || {}, key) && i.credentials[key]));
            if (!donors.length) return null;
            return (
              <div className="space-y-1">
                <Label>Keys</Label>
                <select className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm" value={form.copy_from || ''}
                  onChange={event => set({ copy_from: event.target.value })} data-testid="integration-copy-keys">
                  <option value="">Enter the keys below</option>
                  {donors.map(i => <option key={i.id} value={i.id}>Use the keys saved on {i.name}</option>)}
                </select>
                {form.copy_from && <p className="text-xs text-gray-500">Linked: this connection always uses that connection&apos;s current keys (a refreshed token there applies here too). They are never shown or sent to your browser.</p>}
              </div>
            );
          })()}
          {!form.copy_from && (connector?.fields || []).map(field => (
            <div key={field.key} className="space-y-1">
              <Label>{field.label}{field.required ? '' : ' (optional)'}</Label>
              <Input type={field.secret ? 'password' : 'text'} autoComplete="off"
                placeholder={editing ? (editing.credentials[field.key] || '') : ''}
                value={form.credentials[field.key] ?? ''}
                onChange={event => set({ credentials: { ...form.credentials, [field.key]: event.target.value } })} />
              {field.help && <p className="text-xs text-gray-500">{field.help}</p>}
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Sync every (minutes)</Label>
              <Input type="number" min="15" max="1440" value={form.sync_interval_minutes} onChange={event => set({ sync_interval_minutes: event.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Re-check last (days)</Label>
              {connector?.kind === 'ads' && <p className="text-[11px] text-gray-500">Ad platforms restate figures for up to 14 days; keep 30.</p>}
              {connector?.kind === 'returns' && <p className="text-[11px] text-gray-500">Refunds and dispositions change for weeks; keep 45.</p>}
              <Input type="number" min="1" max="60" value={form.lookback_days} onChange={event => set({ lookback_days: event.target.value })} />
            </div>
          </div>
        </div>
        {connector?.kind === 'returns' && !editing && (
          <p className="rounded bg-blue-50 p-2 text-xs text-blue-900">
            A returns connection starts in <strong>preview</strong>: it reads Amazon&apos;s returns and shows what would be created, but writes
            nothing to Returns, stock or sales until you switch preview off on the connection.
          </p>
        )}
        {connector?.kind === 'sales' && (
          <div className="space-y-1">
            <Label>API is the only source of these sales from (cut-over)</Label>
            <Input type="date" value={form.cutover_date || ''} onChange={event => set({ cutover_date: event.target.value })} data-testid="integration-cutover" />
            <p className="text-xs text-gray-500">Excel uploads and manual entries for this platform on or after this date are skipped, so nothing is counted twice. Leave blank for no cut-over. To also replace Excel rows already uploaded, use Backfill on the connection.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name.trim() || !form.connector} className="bg-[#007AFF] hover:bg-[#0062cc] text-white" data-testid="integration-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RunHistory = ({ integrationId }) => {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    axios.get(`${API}/${integrationId}/runs`).then(r => setRuns(r.data)).catch(() => setRuns([]));
  }, [integrationId]);
  if (!runs) return <p className="text-xs text-gray-500">Loading runs…</p>;
  if (!runs.length) return <p className="text-xs text-gray-500">No syncs yet.</p>;
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-gray-500">
        <tr><th className="py-1">Started</th><th>Trigger</th><th>Result</th><th className="text-right">New</th><th className="text-right">Updated</th><th>Unmatched codes</th></tr>
      </thead>
      <tbody>
        {runs.map(run => (
          <tr key={run.id} className="border-t border-gray-100 align-top">
            <td className="py-1">{when(run.started_at)}</td>
            <td>{run.trigger}</td>
            <td className={run.status === 'FAILED' ? 'text-red-700' : 'text-green-700'}>{run.status === 'FAILED' ? run.error : `Success${run.spend != null ? ` · ${rupees(run.spend)}` : ''}`}</td>
            <td className="text-right">{run.created ?? '—'}</td>
            <td className="text-right">{run.updated ?? '—'}</td>
            <td className="font-mono">{(run.unmatched || []).slice(0, 5).map(u => u.code).join(', ')}{(run.unmatched || []).length > 5 ? ' …' : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Codes from API sales and returns that match no SKU wait in the Code Mappings queue (one place for every source).
const WaitingBanner = ({ country, version }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${ALIAS_API}/queue`, { params: { country } }).then(r => setData(r.data)).catch(() => setData(null));
  }, [country]);
  useEffect(load, [load, version]);
  const totals = data?.totals;
  if (!totals || !totals.codes) return null;
  const sales = totals.by_source?.sales?.codes || 0;
  const returns = totals.by_source?.returns?.codes || 0;
  const pos = totals.by_source?.po?.codes || 0;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status" data-testid="waiting-banner">
      <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
      <span>
        <strong>{totals.codes} code{totals.codes === 1 ? '' : 's'}</strong> waiting for a SKU ({sales} from sales, {returns} from returns{pos ? `, ${pos} from Retail POs` : ''}) hold
        ₹{Math.round(totals.value).toLocaleString('en-IN')} that is <strong>not counted</strong> in Sales, stock or profit yet.
        {totals.strong ? ` ${totals.strong} can be accepted in one click.` : ''}
      </span>
      <Link to="/code-mappings" className="ml-auto font-medium text-amber-900 underline" data-testid="waiting-open-queue">Open the mapping queue</Link>
    </div>
  );
};

const inr = value => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
const monthLabel = m => new Date(`${m}-01T12:00:00Z`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

// One-time history pull: preview what will be replaced, confirm, then watch progress. Undo restores.
const BackfillDialog = ({ open, onOpenChange, item, onStarted }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState('');
  const cutover = item.cutover_date;
  useEffect(() => { if (open) { setDateFrom(cutover || ''); setPreview(null); setConfirm(false); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = async () => {
    setBusy('preview');
    try {
      const { data } = await axios.get(`${API}/${item.id}/backfill/preview`, { params: { date_from: dateFrom } });
      setPreview(data);
      setConfirm(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy('');
    }
  };
  const start = async () => {
    setBusy('start');
    try {
      await axios.post(`${API}/${item.id}/backfill`, { date_from: dateFrom });
      toast.success('Backfill started. Progress shows on the connection.');
      onOpenChange(false);
      onStarted();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy('');
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="backfill-dialog">
        <DialogHeader>
          <DialogTitle>Backfill {platformName(item.platform)} sales from the API</DialogTitle>
          <DialogDescription>
            Pulls orders from the start date to today, month by month. For each month, the Excel and manual rows of this
            platform are replaced by the API's order lines (with order IDs). The replaced rows are kept in a backup and
            Undo brings them back. Afterwards the cut-over moves to the start date.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label>Start date</Label>
            <Input type="date" value={dateFrom} onChange={event => { setDateFrom(event.target.value); setPreview(null); }} data-testid="backfill-date" />
          </div>
          <Button variant="outline" onClick={load} disabled={!dateFrom || !!busy} data-testid="backfill-preview">
            {busy === 'preview' ? 'Checking…' : 'Preview'}
          </Button>
        </div>
        {preview && (
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              {preview.periods} report(s) from {preview.date_from} to {preview.date_to}. Replaces <strong>{preview.excel_rows.toLocaleString('en-IN')}</strong> Excel/manual
              row(s) ({preview.excel_units.toLocaleString('en-IN')} units, {inr(preview.excel_value)}).
              {preview.other_api_rows > 0 && <> Also takes over {preview.other_api_rows.toLocaleString('en-IN')} row(s) stored by an older connection (backed up, restored by Undo).</>}
            </p>
            {preview.months.length > 0 && (
              <div className="max-h-56 overflow-y-auto rounded border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-left text-gray-500">
                    <tr><th className="p-1.5">Month</th><th className="p-1.5 text-right">Excel rows replaced</th><th className="p-1.5 text-right">Units</th>
                      <th className="p-1.5 text-right">Value</th><th className="p-1.5 text-right">API rows already there</th></tr>
                  </thead>
                  <tbody>
                    {preview.months.map(m => (
                      <tr key={m.month} className="border-t border-gray-100">
                        <td className="p-1.5">{monthLabel(m.month)}</td>
                        <td className="p-1.5 text-right tabular-nums">{m.excel_rows}</td>
                        <td className="p-1.5 text-right tabular-nums">{m.excel_units}</td>
                        <td className="p-1.5 text-right tabular-nums">{inr(m.excel_value)}</td>
                        <td className="p-1.5 text-right tabular-nums">{m.api_rows}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <label className="flex items-start gap-2 text-sm text-gray-800">
              <input type="checkbox" className="mt-1" checked={confirm} onChange={e => setConfirm(e.target.checked)} data-testid="backfill-confirm" />
              Replace these Excel/manual rows with the API's data from {preview.date_from}, and accept only API data for
              {' '}{platformName(item.platform)} from that date on.
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={start} disabled={!preview || !confirm || !!busy} className="bg-[#007AFF] hover:bg-[#0062cc] text-white" data-testid="backfill-start">
            {busy === 'start' ? 'Starting…' : 'Start backfill'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BACKFILL_TONE = { RUNNING: 'text-blue-700', DONE: 'text-green-700', FAILED: 'text-red-700', UNDONE: 'text-gray-500' };

const BackfillHistory = ({ item, version, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [undoing, setUndoing] = useState(false);
  const load = useCallback(() => {
    // A failed poll (e.g. a server restart) keeps the last known rows and keeps polling
    axios.get(`${API}/${item.id}/backfills`).then(r => setRows(r.data)).catch(() => {});
  }, [item.id]);
  useEffect(load, [load, version]);
  const running = rows.some(r => r.status === 'RUNNING');
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [running, load]);
  const wasRunning = React.useRef(false);
  useEffect(() => { if (wasRunning.current && !running) onChanged(); wasRunning.current = running; }, [running, onChanged]);
  if (!rows.length) return null;
  const undo = async row => {
    if (!window.confirm(`Undo the backfill from ${row.date_from}? API rows it added are removed, the replaced Excel rows come back, and auto-sync for this connection is turned off.`)) return;
    setUndoing(true);
    try {
      const { data } = await axios.post(`${API}/backfills/${row.id}/undo`);
      toast.success(data.message);
      load();
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUndoing(false);
    }
  };
  // Same rule as the server: only the platform's latest backfill, and not while one is running
  const undoableId = item.undoable_backfill_id;
  return (
    <div className="space-y-1 rounded border border-gray-200 p-2 text-xs" data-testid="backfill-history">
      {rows.slice(0, 3).map(row => (
        <div key={row.id} className="flex flex-wrap items-center gap-2">
          <History className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
          <span>Backfill from {row.date_from}</span>
          <span className={`font-medium ${BACKFILL_TONE[row.status] || ''}`}>
            {row.status === 'RUNNING' ? `running: report ${Math.min(row.periods_done + 1, row.periods_total)} of ${row.periods_total}${row.current ? ` (${row.current})` : ''}` : row.status.toLowerCase()}
          </span>
          {row.status !== 'RUNNING' && row.status !== 'UNDONE' && (
            <span className="text-gray-600">{row.created} new · {row.excel_replaced} Excel rows replaced · {row.waiting_for_sku} waiting for a SKU</span>
          )}
          {row.error && <span className="text-red-700">{row.error}</span>}
          {!running && row.id === undoableId && (
            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => undo(row)} disabled={undoing} data-testid="backfill-undo">
              <Undo2 className="mr-1 h-3.5 w-3.5" /> {undoing ? 'Undoing…' : 'Undo'}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

const toBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Returns connections: preview (shadow) mode, per-report health, the last preview and the Seller Central file fallback.
const ReturnsPanel = ({ item, version, onChanged }) => {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState('');
  const preview = item.settings?.shadow_mode !== false;
  useEffect(() => {
    axios.get(`${API}/${item.id}/runs`).then(r => setRun(r.data.find(x => x.status === 'SUCCESS') || null)).catch(() => setRun(null));
  }, [item.id, version]);

  const goLive = async () => {
    if (!window.confirm('Switch preview off? From today Amazon returns are written to Returns, net sales after returns starts this month, and every row read so far is promoted on the next sync.')) return;
    setBusy('live');
    try {
      await axios.put(`${API}/${item.id}`, { settings: { shadow_mode: false } });
      toast.success('Returns are live. The next sync promotes what was read in preview.');
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy('');
    }
  };

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy('upload');
    try {
      const { data } = await axios.post(`${RETURNS_API}/import/amazon`, { file: await toBase64(file), filename: file.name, country: item.country });
      toast.success(`${REPORT_NAMES[data.report]} file: ${data.new_rows} new row(s), ${data.changed_rows} changed, ${data.unchanged_rows} unchanged${data.waiting_for_sku ? `, ${data.waiting_for_sku} waiting for a SKU` : ''}${data.mode === 'preview' ? ' (preview: nothing written to Returns yet)' : ''}.`);
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy('');
    }
  };

  const reports = Object.entries(item.report_status || {});
  const months = Object.entries(run?.net_sales_change_by_month || {});
  const stock = (run?.stock_if_promoted || []).filter(r => r.fba_sellable_units);
  return (
    <div className="space-y-2 rounded border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700" data-testid="returns-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {preview
          ? <p><span className="rounded bg-blue-100 px-1.5 py-0.5 font-medium text-blue-800">Preview</span> Reads Amazon&apos;s returns and shows what would happen. Nothing is written to Returns, stock or sales.</p>
          : <p><span className="rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800">Live</span> Amazon returns are written to Returns and deducted from Amazon API sales (net sales after returns).</p>}
        <div className="flex gap-2">
          <label className={`inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-2 py-1 font-medium ${busy ? 'opacity-50' : ''}`}>
            {busy === 'upload' ? 'Uploading…' : 'Upload Seller Central file'}
            <input type="file" accept=".txt,.tsv,.csv,.xlsx" className="hidden" onChange={upload} disabled={!!busy} data-testid="returns-upload" />
          </label>
          {preview && <Button size="sm" onClick={goLive} disabled={!!busy} className="h-7 bg-green-700 text-white hover:bg-green-800" data-testid="returns-go-live">{busy === 'live' ? 'Switching…' : 'Switch preview off'}</Button>}
        </div>
      </div>
      {reports.length > 0 && (
        <ul className="space-y-0.5">
          {reports.map(([report, state]) => (
            <li key={report} className={state.last_error ? 'text-red-700' : 'text-green-800'}>
              {REPORT_NAMES[report] || report}: {state.last_error ? state.last_error : `read ${when(state.last_success_at)}`}
            </li>
          ))}
        </ul>
      )}
      {run && run.mode === 'preview' && (
        <div className="space-y-1" data-testid="returns-preview">
          <p className="font-medium text-gray-900">Last preview ({when(run.started_at)}): if preview were off now</p>
          <p>
            {run.would_create?.FBA || 0} FBA and {run.would_create?.MFN || 0} seller-fulfilled return(s) would be created
            {run.would_attach?.length ? `, ${run.would_attach.length} attached to returns your team already entered (${run.would_attach.slice(0, 5).join(', ')}${run.would_attach.length > 5 ? ' …' : ''})` : ''}
            {run.would_update ? `, ${run.would_update} updated` : ''}
            {run.waiting_for_sku ? `; ${run.waiting_for_sku} row(s) wait for a SKU` : ''}.
          </p>
          {months.length > 0 && <p>Change to Amazon API sales: {months.map(([m, v]) => `${monthLabel(m)} ${inr(v)}`).join(' · ')} (estimate; only from the go-live month once live).</p>}
          {stock.length > 0 && <p>FBA units that would go back to Amazon sellable stock: {stock.slice(0, 10).map(r => `${r.sku_code} +${r.fba_sellable_units}`).join(', ')}. Returns dated before go-live post no stock.</p>}
        </div>
      )}
    </div>
  );
};

const ConnectionCard = ({ item, onEdit, onRemove, onChanged }) => {
  const [busy, setBusy] = useState('');
  const [showRuns, setShowRuns] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillVersion, setBackfillVersion] = useState(0);
  const [runsKey, setRunsKey] = useState(0);

  const act = async (kind, request) => {
    setBusy(kind);
    try {
      const { data } = await request();
      if (kind === 'test') (data.ok ? toast.success : toast.error)(data.message);
      if (kind === 'sync') {
        if (data.status === 'FAILED') toast.error(data.error);
        else if (data.mode === 'preview') toast.success(`Preview: ${data.would_create?.FBA || 0} FBA and ${data.would_create?.MFN || 0} seller-fulfilled return(s) would be created${data.waiting_for_sku ? `, ${data.waiting_for_sku} waiting for a SKU` : ''}. Nothing was written.${data.warning ? ` ${data.warning}` : ''}`);
        else if (data.mode === 'live') toast.success(`Returns synced: ${data.created} new, ${data.updated} updated, ${data.attached} attached${data.waiting_for_sku ? `, ${data.waiting_for_sku} waiting for a SKU` : ''}${data.warning ? `. ${data.warning}` : ''}`);
        else toast.success(`Synced${data.spend != null ? ` ${rupees(data.spend)} ad spend` : ''}: ${data.created} new, ${data.updated} updated${data.replaced_from_other_source ? `, ${data.replaced_from_other_source} taken over from the previous source` : ''}${data.waiting_for_sku ? `, ${data.waiting_for_sku} waiting for a SKU` : ''}${data.unmatched?.length ? `, ${data.unmatched.length} unmatched codes` : ''}`);
        setRunsKey(k => k + 1);
      }
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3" data-testid={`integration-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{item.name}</h3>
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[item.status] || STATUS_TONE.NEW}`}>{item.status}</span>
          </div>
          <p className="text-xs text-gray-600">{item.connector} → {item.kind === 'ads' ? `ad spend for ${platformName(item.platform)}` : item.kind === 'returns' ? `returns for ${platformName(item.platform)}` : platformName(item.platform)} · every {item.settings?.sync_interval_minutes} min · re-checks last {item.settings?.lookback_days} days</p>
          {item.kind === 'sales' && (item.cutover_date
            ? <p className="text-xs text-green-800" data-testid="cutover-on">API is the only source of {platformName(item.platform)} sales from <strong>{item.cutover_date}</strong>; Excel uploads for those dates are skipped, and the sync never writes earlier days.</p>
            : <p className="text-xs text-amber-800" data-testid="cutover-off">No cut-over set: Excel uploads for {platformName(item.platform)} are still accepted for any date, so the same day can be counted twice. Set one with Backfill or Edit.</p>)}
          {!item.connector_available && <p className="text-xs text-red-700">This connector is no longer installed on the server.</p>}
          {item.credentials_from_name && <p className="text-xs text-gray-600">Keys: linked to {item.credentials_from_name}</p>}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          Auto-sync
          <Switch checked={item.enabled} onCheckedChange={value => act('toggle', () => axios.put(`${API}/${item.id}`, { enabled: value }))} disabled={!!busy} />
        </label>
      </div>
      <div className="grid gap-2 text-xs text-gray-600 sm:grid-cols-3">
        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Last sync: {when(item.last_sync_at)}</span>
        <span className="flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Next: {item.enabled ? when(item.next_sync_at) : 'auto-sync off'}</span>
        <span>Last success: {when(item.last_success_at)}</span>
      </div>
      {item.last_error && <p className="rounded bg-red-50 p-2 text-xs text-red-800">{item.last_error}</p>}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('test', () => axios.post(`${API}/${item.id}/test`))}>
          <Wifi className="mr-1 h-4 w-4" /> {busy === 'test' ? 'Testing…' : 'Test'}
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('sync', () => axios.post(`${API}/${item.id}/sync`))}>
          <PlayCircle className="mr-1 h-4 w-4" /> {busy === 'sync' ? 'Syncing…' : 'Sync now'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onEdit(item)}><Pencil className="mr-1 h-4 w-4" /> Edit</Button>
        <Button size="sm" variant="outline" onClick={() => setShowRuns(v => !v)}>{showRuns ? 'Hide history' : 'Sync history'}</Button>
        {item.kind === 'sales' && item.connector_available && (
          <Button size="sm" variant="outline" onClick={() => setBackfillOpen(true)} data-testid="backfill-open"><History className="mr-1 h-4 w-4" /> Backfill</Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onRemove(item)}><Trash2 className="mr-1 h-4 w-4 text-red-600" /> Remove</Button>
      </div>
      {item.kind === 'returns' && <ReturnsPanel item={item} version={runsKey + (item.last_sync_at ? 1 : 0)} onChanged={onChanged} />}
      {showRuns && <RunHistory key={runsKey} integrationId={item.id} />}
      {item.kind === 'sales' && <BackfillHistory item={item} version={backfillVersion} onChanged={onChanged} />}
      {item.kind === 'sales' && (
        <BackfillDialog open={backfillOpen} onOpenChange={setBackfillOpen} item={item}
          onStarted={() => { setBackfillVersion(v => v + 1); onChanged(); }} />
      )}
    </div>
  );
};

export default function IntegrationsPage() {
  const { country } = useCountry();
  const [connectors, setConnectors] = useState([]);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [removing, setRemoving] = useState(null);
  const [version, setVersion] = useState(0);

  const load = useCallback(() => {
    setError(null);
    Promise.all([axios.get(`${API}/connectors`), axios.get(`${API}?country=${country}`)])
      .then(([c, i]) => { setConnectors(c.data); setItems(i.data); setVersion(v => v + 1); })
      .catch(setError);
  }, [country]);
  useEffect(load, [load]);

  const available = connectors.filter(c => c.available);
  const planned = connectors.filter(c => !c.available);

  return (
    <div className="space-y-5" data-testid="integrations-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Integrations</h1>
          <p className="mt-1 text-sm text-gray-600">Admin only. Pull sales into Sales records, ad spend into Profitability, and Amazon returns into Returns, automatically.</p>
        </div>
        <Button onClick={() => setDialog({ open: true, editing: null })} disabled={!available.length}
          className="bg-[#007AFF] hover:bg-[#0062cc] text-white" data-testid="integration-add">
          <Plus className="mr-2 h-4 w-4" /> Add connection
        </Button>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        Once a platform syncs by API, stop uploading that platform&apos;s sales in Excel for the same dates, or they will be counted twice.
      </div>

      {error && <LoadError what="integrations" error={error} onRetry={load} />}

      <WaitingBanner country={country} version={version} />

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-600">
          <Plug className="mx-auto mb-2 h-6 w-6 text-gray-400" />
          No connections yet.{available.length ? ' Click “Add connection” to connect a marketplace.' : ' Marketplace connectors are coming soon (see below).'}
        </div>
      )}
      <div className="space-y-3">
        {(items || []).map(item => (
          <ConnectionCard key={item.id} item={item} onChanged={load}
            onEdit={editing => setDialog({ open: true, editing })} onRemove={setRemoving} />
        ))}
      </div>

      {planned.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-900">Coming soon</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {planned.map(c => (
              <div key={c.key} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
                <Link2 className="h-4 w-4 text-gray-400" />
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-gray-500">→ {c.platforms.map(platformName).join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConnectionDialog open={dialog.open} editing={dialog.editing} connectors={connectors} country={country} items={items || []}
        onOpenChange={open => setDialog(prev => ({ ...prev, open }))} onSaved={load} />
      <ConfirmDialog open={!!removing} onOpenChange={open => { if (!open) setRemoving(null); }}
        title={`Remove ${removing?.name}?`} description="Saved keys are deleted. Data it already imported is kept."
        confirmLabel="Remove connection" destructive
        onConfirm={async () => {
          try {
            await axios.delete(`${API}/${removing.id}`);
            toast.success('Connection removed');
            setRemoving(null);
            load();
          } catch (err) {
            toast.error(errorMessage(err));
            throw err;
          }
        }} />
    </div>
  );
}
